import axios from 'axios';

// Fallback logic to fix pipeline injection issues
let BASE = process.env.REACT_APP_API_URL;
if (!BASE || BASE.includes('REACT_APP_API_URL')) {
  BASE = 'https://ets-api-b8gsf8ctg4asgce6.eastasia-01.azurewebsites.net/api';
} else if (BASE === 'http://localhost:5000/api' && window.location.hostname !== 'localhost') {
  BASE = 'https://ets-api-b8gsf8ctg4asgce6.eastasia-01.azurewebsites.net/api';
}

const STATIC_BASE = BASE.replace('/api', ''); // https://ets-api-b8gsf8ctg4asgce6.eastasia-01.azurewebsites.net

const api = axios.create({ 
  baseURL: BASE,
  timeout: 30000 // 30-second network safety guard to prevent hung sockets from exhausting browser connection limits
});

export const resolveImage = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) return path;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${STATIC_BASE}${cleanPath}`;
};

// Attach token automatically
api.interceptors.request.use(config => {
  const token = localStorage.getItem('ets_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401/403 globally (redirect to login if unauthorized, with loop protection)
let isRedirecting = false;
api.interceptors.response.use(
  res => res,
  err => {
    if ((err.response?.status === 401 || err.response?.status === 403) && !isRedirecting) {
      isRedirecting = true;
      localStorage.removeItem('ets_token');
      localStorage.removeItem('ets_admin');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
  uploadProfilePhoto: (formData) => api.post('/upload/profile-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
};

export const passwordResetAPI = {
  /** Search users by type and query string */
  search: (userType, q) => api.get('/admin/password-reset/search', { params: { userType, q } }),
  /** Reset a specific user's password; returns { tempPassword } once */
  reset: (userType, userId) => api.post(`/admin/password-reset/${userType}/${userId}`),
};


export const approvalsAPI = {
  getPendingApprovalsCount: () => api.get('/admin/approvals/pending-count'),
  getPending: () => api.get('/admin/approvals/registrations/pending'),
  process: (id, action, rejectionReason) => api.post('/admin/approvals/registrations/process', { 
    requestId: id, 
    status: action === 'Approve' ? 'Approved' : 'Rejected', 
    adminNote: rejectionReason 
  }),
  getPendingWithdrawals: (status) => api.get('/admin/approvals/withdrawals/pending', { params: status ? { status } : {} }),
  processWithdrawal: (studentId, action, adminNote) => api.post('/admin/approvals/withdrawals/process', {
    studentId,
    action,
    adminNote
  }),
};

export const dashboardAPI = {
  getOverview: () => api.get('/admin/dashboard/stats'),
};

export const tutorsAPI = {
  getAll: () => api.get('/admin/tutors'),
  getById: (id) => api.get(`/admin/tutors/${id}`),
  create: (data) => {
    const isFormData = data instanceof FormData;
    return api.post('/admin/tutors', data, {
      headers: { 'Content-Type': isFormData ? 'multipart/form-data' : 'application/json' }
    });
  },
  update: (id, data) => api.put(`/admin/tutors/${id}`, data),
  uploadPhoto: (id, formData) => api.post(`/admin/tutors/${id}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  toggleStatus: (id, status) => api.patch(`/admin/tutors/${id}/status`, { status }), // New
  delete: (id) => api.delete(`/admin/tutors/${id}`),
};

export const parentsAPI = {
  getAll: () => api.get('/admin/parents'),
  update: (id, data) => api.put(`/admin/parents/${id}`, data),
  toggleStatus: (id, status) => api.patch(`/admin/parents/${id}/status`, { status }),
  delete: (id) => api.delete(`/admin/parents/${id}`),
};

export const uploadAPI = {
  tutorPhoto: (formData) => api.post('/upload/tutor-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  studentPhoto: (formData) => api.post('/upload/student-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  batchImage: (formData) => api.post('/upload/batch-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  merchandiseImage: (formData) => api.post('/upload/merchandise-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  paymentReceipt: (formData) => api.post('/upload/payment-receipt', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  qrCode: (formData) => api.post('/upload/qr-code', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

export const batchesAPI = {
  getAll: () => api.get('/admin/batches'),
  getById: (id) => api.get(`/admin/batches/${id}`),
  getDeepDetails: (id) => api.get(`/admin/batches/${id}/detail`),
  create: (data) => api.post('/admin/batches', data),
  update: (id, data) => api.put(`/admin/batches/${id}`, data),
  addStudent: (batchId, data) => api.post(`/admin/batches/${batchId}/students`, data),
  removeStudent: (batchId, studentId) => api.delete(`/admin/batches/${batchId}/students/${studentId}`),
  approveEdit: (id) => api.post(`/admin/batches/${id}/approve`),
  rejectEdit: (id) => api.post(`/admin/batches/${id}/reject`),
  delete: (id) => api.delete(`/admin/batches/${id}`),
};

export const studentsAPI = {
  getAll: () => api.get('/admin/students'),
  getById: (id) => api.get(`/admin/students/${id}`),
  create: (data) => api.post('/admin/students', data),
  update: (id, data) => api.put(`/admin/students/${id}`, data),
  toggleStatus: (id, status) => api.patch(`/admin/students/${id}/status`, { status }),
  delete: (id) => api.delete(`/admin/students/${id}`),
  addNote: (id, note) => api.post(`/admin/students/${id}/notes`, { note }),
  updateObjective: (id, objId, progress) => api.put(`/admin/students/${id}/objectives/${objId}`, { progress }),
  verifyObjective: (id, objId) => api.post(`/admin/students/${id}/objectives/${objId}/verify`),
  promote: (id, from, to) => api.post(`/admin/students/${id}/promote`, { from, to }),
  // Manual Fee Verification for Batch Financial Hub
  updateStatus: (id, feeStatus) => api.put(`/admin/students/${id}`, { feeStatus })
};

export const enquiriesAPI = {
  getAll: () => api.get('/admin/enquiries'),
  create: (data) => axios.post(`${BASE}/enquiries`, data), // truly public
  update: (id, data) => api.put(`/admin/enquiries/${id}`, data),
  delete: (id) => api.delete(`/admin/enquiries/${id}`),
};

export const libraryAPI = {
  getAll: () => api.get('/library'),
  uploadVideo: (file, title) => {
    return api.post('/video/upload', file, {
      headers: {
        'Content-Type': 'video/mp4',
        'X-Video-Title': title || 'Untitled Video'
      }
    });
  },
  submitItem: (data) => {
    const isFormData = data instanceof FormData;
    return api.post('/library/submit', data, {
      headers: { 'Content-Type': isFormData ? 'multipart/form-data' : 'application/json' }
    });
  },
  updateItem: (itemId, data) => api.put(`/library/items/${itemId}`, data),
  deleteItem: (itemId) => api.delete(`/library/items/${itemId}`),
  reviewItem: (data) => api.post('/library/review', data),
  getBunnyStats: () => api.get('/video/account-stats'),
  createCategory: (data) => api.post('/library/categories', data),
  updateCategory: (id, data) => api.put(`/library/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/library/categories/${id}`),
};

export const settingsAPI = {
  get: () => api.get('/admin/settings'),
  update: (data) => api.post('/admin/settings', data),
  testEmail: () => api.post('/admin/settings/test-email'),
};

export const attendanceAPI = {
  mark: (data) => api.post('/attendance', data),
  getSummary: (date) => api.get('/attendance/summary', { params: { date } }),
  getDebugAll: () => api.get('/attendance/debug-all'),
  getUserHistory: (userId) => api.get(`/attendance/user/${userId}`),
  getBatchSheet: (batchId, date, role) => api.get(`/attendance/batch/${batchId}`, { params: { date, role } }),
  getAdminTutors: (params) => api.get('/attendance/admin/tutors', { params }),
  getStudentMonthly: (studentId, month, batchId) => api.get(`/attendance/student/${studentId}/monthly`, { params: { month, batchId } }),
  delete: (id) => api.delete(`/attendance/${id}`),
};

export const closuresAPI = {
  getPreview: (date, scope, batchIds, rescheduleDate, type) => api.get('/closures/preview', { 
    params: { 
      date, 
      scope, 
      batchIds: Array.isArray(batchIds) ? batchIds.join(',') : batchIds,
      ...(rescheduleDate ? { rescheduleDate } : {}),
      ...(type ? { type } : {})
    } 
  }),
  getAll: () => api.get('/closures'),
  create: (data) => api.post('/closures', data),
  delete: (id) => api.delete(`/closures/${id}`),
};

export const financeAPI = {
  getSummary: () => api.get('/admin/finance/summary'),
  getStudentBalances: (params) => api.get('/admin/finance/student-balances', { params }),
  getStudentFeeHistory: (studentId, batchId) => api.get(`/admin/finance/student-fee-history/${studentId}${batchId ? `?batchId=${batchId}` : ''}`),
};

export const paymentsAPI = {
  getAll: () => api.get('/admin/payments'),
  verify: (id, status, rejectionReason) => api.put(`/admin/payments/${id}/verify`, { status, rejectionReason }),
  reportIssue: (id, data) => api.post(`/admin/payments/${id}/issue`, data),
  submit: (data) => api.post('/payments/submit', data), // public for students/parents
  submitMultiple: (data) => api.post('/payments/submit-multiple', data), // admin desk multi-month
};

export const landingAPI = {
  getContent: () => api.get('/landing'),
  updateContent: (data) => api.put('/admin/landing', data),
  uploadHeroImage: (formData) => api.post('/upload/hero-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadAboutImage: (formData) => api.post('/upload/about-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getPublicBatches: () => axios.get(`${BASE}/batches/public`), // truly public
  getPublicTutors: () => axios.get(`${BASE}/tutors/public`), // truly public
  getPublicMerchandise: () => axios.get(`${BASE}/merchandise/public`), // truly public
  getPublicSettings: () => axios.get(`${BASE}/settings`) // truly public
};

export const notificationsAPI = {
  send: (studentId, targets, month, customMessage, customMessageZh) => api.post('/notifications/send', { studentId, targets, month, customMessage, customMessageZh }),
  getHistory: (studentId) => api.get(`/notifications/admin/${studentId}`),
  getMy: (params) => api.get('/notifications', { params }),
  markAllAsRead: () => api.patch('/notifications/read-all'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
};

export const merchandiseAPI = {
  getAll: () => api.get('/admin/merchandise'),
  create: (data) => api.post('/admin/merchandise', data),
  update: (id, data) => api.put(`/admin/merchandise/${id}`, data),
  delete: (id) => api.delete(`/admin/merchandise/${id}`),
};

export default api;


// Unified Translation Service pointing to backend
export const translationAPI = {
  translate: async (text, from = 'en', to = 'zh-TW') => {
    try {
      const res = await api.post('/translate', { text, from, to });
      return { responseData: { translatedText: res.data.translated } };
    } catch (e) {
      console.error('Translation error:', e);
      return { responseData: { translatedText: text } };
    }
  }
};
